import axios from "axios";
import tokenManager from "../utils/tokenManager";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 30000,
      withCredentials: true, // Enable cookies
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        if (tokenManager.shouldRefreshToken()) {
          try {
            await tokenManager.refreshAccessToken();
          } catch (error) {
            console.warn("Pre-request token refresh failed:", error);
          }
        }
        let token = tokenManager.getAccessToken();

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          // Don't proceed if we can't get a token for protected routes
          if (!config.url.includes("/auth/")) {
            console.warn("No access token available for protected route");
          }
        }

        // Add CSRF token for state-changing requests
        if (
          ["POST", "PUT", "DELETE", "PATCH"].includes(
            config.method.toUpperCase(),
          )
        ) {
          const csrfToken = tokenManager.getCSRFToken();
          if (csrfToken) {
            config.headers["X-CSRF-Token"] = csrfToken;
          }
        }

        // Add fingerprint
        const fingerprint = await tokenManager.getFingerprint();
        if (fingerprint) {
          config.headers["X-Fingerprint"] = fingerprint;
        }

        // Set content type
        if (!(config.data instanceof FormData)) {
          config.headers["Content-Type"] = "application/json";
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const originalRequest = error.config;

        // Handle token expiration
        if (
          error.response?.status === 401 &&
          error.response?.data?.code === "TOKEN_EXPIRED" &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            const newToken = await tokenManager.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            if (refreshError.response?.status === 401) {
              tokenManager.clearCurrentSession();
              window.location.href = "/login?reason=session_expired";
            }

            return Promise.reject(refreshError);
          }
        }

        // Handle session timeout
        if (error.response?.data?.code === "SESSION_TIMEOUT") {
          tokenManager.clearCurrentSession();
        }

        // Handle account lockout
        if (error.response?.data?.code === "ACCOUNT_LOCKED") {
          window.dispatchEvent(
            new CustomEvent("accountLocked", {
              detail: { message: error.response.data.message },
            }),
          );
        }

        return Promise.reject(error);
      },
    );
  }

  // Auth endpoints with enhanced security
  async register(userData) {
    try {
      const response = await this.client.post("/auth/register", userData);

      if (response.success) {
        tokenManager.setTokens(
          response.accessToken,
          response.refreshToken,
          response.csrfToken,
        );

        if (response.fingerprint) {
          tokenManager.setFingerprint(response.fingerprint);
        }

        if (response.user) {
          tokenManager.setUser(response.user);
        }
      }

      return response;
    } catch (error) {
      console.error("Register API error:", error);
      throw error;
    }
  }

  async login(email, password, remember = false) {
    const fingerprint = await tokenManager.getFingerprint();

    const response = await this.client.post("/auth/login", {
      email,
      password,
      remember,
      fingerprint,
    });

    if (response.success) {
      tokenManager.setTokens(
        response.accessToken,
        response.refreshToken,
        response.csrfToken,
      );

      if (response.fingerprint) {
        tokenManager.setFingerprint(response.fingerprint);
      }

      if (response.user) {
        tokenManager.setUser(response.user);
      }
    }

    return response;
  }

  async logout(allDevices = false) {
    const refreshToken = tokenManager.getRefreshToken();

    try {
      await this.client.post("/auth/logout", {
        refreshToken,
        allDevices,
      });
    } finally {
      if (allDevices) {
        tokenManager.clearAll();
      } else {
        tokenManager.clearCurrentSession();
      }
    }
  }
  // ─── Demo Login ───────────────────────────────────────────────────────────
  // Add this method to the ApiService class in api.js

  async demoLogin({ name, email, companyName }) {
    const response = await this.client.post("/auth/demo-login", {
      name,
      email,
      companyName,
    });

    if (response.success) {
      tokenManager.setTokens(
        response.accessToken,
        response.refreshToken,
        response.csrfToken,
      );

      if (response.fingerprint) {
        tokenManager.setFingerprint(response.fingerprint);
      }

      if (response.user) {
        tokenManager.setUser(response.user);
      }
    }

    return response;
  }
  async getSessions() {
    return this.client.get("/auth/sessions");
  }

  async revokeSession(sessionId) {
    return this.client.delete(`/auth/sessions/${sessionId}`);
  }

  async getMe() {
    return this.client.get("/auth/me");
  }

  async forgotPassword(email) {
    return this.client.post("/auth/forgot-password", { email });
  }

  // Onboarding endpoints
  async updateCompanyDetails(companyData) {
    return this.client.put("/onboarding/company", companyData);
  }

  async inviteTeamMembers(emails) {
    return this.client.post("/onboarding/invite-team", { emails });
  }

  async uploadAsset(file) {
    const formData = new FormData();
    formData.append("file", file);
    return this.client.post("/onboarding/upload-assets", formData);
  }

  async updateWorkspaceUrl(workspaceUrl) {
    return this.client.put("/onboarding/workspace-url", { workspaceUrl });
  }

  async validateInvitation(token) {
    return this.client.get(`/auth/invitation/${token}`);
  }

  async acceptInvitation(data) {
    return this.client.post("/auth/accept-invitation", data);
  }

  // Team management
  async getTeamMembers() {
    return this.client.get("/team/members");
  }

  async getLicenseSummary() {
    return this.client.get("/licenses/summary");
  }

  async updateUserProductAccess(
    userId,
    { hasOctopushXAccess, hasRecordingStudioAccess },
  ) {
    return this.client.put(`/team/member/${userId}/access`, {
      hasOctopushXAccess,
      hasRecordingStudioAccess,
    });
  }

  // Replace the existing inviteTeamMember with this richer version
  async inviteTeamMember(email, role = "member", grants = {}) {
    return this.client.post("/team/invite", {
      email,
      role,
      grants: {
        octopushX: !!grants.octopushX,
        recordingStudio: !!grants.recordingStudio,
      },
    });
  }

  async updateUserRole(userId, role) {
    return this.client.put(`/team/member/${userId}/role`, { role });
  }

  async removeUser(userId) {
    return this.client.delete(`/team/member/${userId}`);
  }

  async resendInvitation(invitationId) {
    return this.client.post(`/team/invitation/${invitationId}/resend`);
  }

  async cancelInvitation(invitationId) {
    return this.client.delete(`/team/invitation/${invitationId}`);
  }

  // Profile management
  async updatePersonalDetails(data) {
    return this.client.put("/auth/update-profile", data);
  }

  async uploadProfileImage(formData) {
    return this.client.post("/auth/upload-profile-image", formData);
  }

  // Company endpoints
  async updateCompanyData(data) {
    return this.client.put("/company/update", data);
  }

  async uploadCompanyLogo(formData) {
    return this.client.post("/company/upload-logo", formData);
  }

  async scheduleMeeting(meetingData) {
    return this.client.post("/meetings/schedule", meetingData);
  }

  async getMeetings(filters = {}) {
    return this.client.get("/meetings", { params: filters });
  }

  async getMeetingDetails(meetingId) {
    return this.client.get(`/meetings/${meetingId}`);
  }

  async stopRecording(meetingId) {
    return this.client.post(`/meetings/${meetingId}/stop`);
  }

  async retryMeeting(meetingId) {
    return this.client.post(`/meetings/${meetingId}/retry`);
  }

  // Call management endpoints
  async uploadCallRecording(formData) {
    return this.client.post("/calls/upload", formData);
  }

  async getCallDetails(callId) {
    return this.client.get(`/calls/${callId}`);
  }

  async getCallAnalytics(filters = {}) {
    return this.client.get("/calls/analytics", { params: filters });
  }
  async getCallAnalyticsByAgents(filters = {}) {
    return this.client.get("/calls/analytics/agents", { params: filters });
  }
  async updateCallMetadata(callId, data) {
    return this.client.patch(`/calls/${callId}`, data);
  }

  async exportCalls(filters = {}) {
    const response = await this.client.get("/calls/export/data", {
      params: filters,
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `calls-export-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // ====== TEAMS (Tenant + Managed Users) ======
  async connectTeams() {
    return this.client.post("/teams/connect");
  }
  async getTeamsStatus() {
    return this.client.get("/teams/status");
  }
  async disconnectTeams() {
    return this.client.post("/teams/disconnect");
  }
  async listTeamsTenantUsers(search = "") {
    return this.client.get("/teams/tenant-users", { params: { search } });
  }
  async getManagedTeamsUsers() {
    return this.client.get("/teams/managed-users");
  }
  async addManagedTeamsUsers(users) {
    return this.client.post("/teams/managed-users", { users });
  }
  async removeManagedTeamsUser(managedUserId) {
    return this.client.delete(`/teams/managed-users/${managedUserId}`);
  }
  async toggleManagedTeamsUser(managedUserId, enabled) {
    return this.client.patch(`/teams/managed-users/${managedUserId}/toggle`, {
      enabled,
    });
  }
  async syncManagedTeamsUser(managedUserId) {
    return this.client.post(`/teams/managed-users/${managedUserId}/sync`);
  }
  async syncTeamsAll() {
    return this.client.post("/teams/sync");
  }
  async getTeamsUpcomingMeetings() {
    return this.client.get("/teams/meetings/upcoming");
  }
  async joinTeamsMeeting(meetingId) {
    return this.client.post(`/teams/meetings/${meetingId}/join`);
  }
  async leaveTeamsMeeting(meetingId) {
    return this.client.post(`/teams/meetings/${meetingId}/leave`);
  }
  async askGenziStream(query, onChunk, options = {}) {
    const response = await fetch(`${API_URL}/ai/assistant/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokenManager.getAccessToken()}`,
        "X-CSRF-Token": tokenManager.getCSRFToken(),
        "X-Fingerprint": await tokenManager.getFingerprint(),
      },
      body: JSON.stringify({
        query,
        context: options.context,
        sessionId: options.sessionId, // ADD THIS
        conversationContext: options.conversationContext,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const data = line.slice(6);
          if (data === "[DONE]") return;
          try {
            onChunk(data);
          } catch (e) {
            console.error("Error processing chunk:", e);
          }
        }
      }
    }
  }

  // Also add a regular askGenzi method with sessionId support
  async askGenzi(query, options = {}) {
    return this.client.post("/ai/assistant/query", {
      query,
      context: options.context,
      preferences: options.preferences,
      sessionId: options.sessionId, // ADD THIS
    });
  }

  async askGenziVoice(audioBlob, options = {}) {
    const formData = new FormData();
    formData.append("audio", audioBlob, "voice.webm");
    formData.append("language", options.language || "en-US");
    formData.append("wantAudioResponse", options.wantAudioResponse || false);

    return this.client.post("/ai/assistant/voice", formData);
  }

  async getSuggestions(params = {}) {
    return this.client.get("/ai/assistant/suggestions", { params });
  }

  async sendFeedback(feedbackData) {
    return this.client.post("/ai/assistant/feedback", feedbackData);
  }

  async exportConversation(conversationId, format = "json") {
    return this.client.get(`/ai/assistant/export/${conversationId}`, {
      params: { format },
      responseType: format === "pdf" ? "blob" : "json",
    });
  }

  async updateUserExtension(userId, extensionNumber) {
    return this.client.put(`/team/member/${userId}/extension`, {
      extensionNumber,
    });
  }

  async downloadTranscript(meetingId, format = "txt") {
    const response = await this.client.get(
      `/meetings/${meetingId}/transcript`,
      {
        params: { format },
        responseType: "blob",
      },
    );

    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `transcript-${meetingId}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async deleteMeeting(meetingId) {
    return this.client.delete(`/meetings/${meetingId}`);
  }
  async retryMeetingProcessing(meetingId) {
    return this.client.post(`/teams/meetings/${meetingId}/retry`);
  }
  async getActiveMeetings() {
    return this.client.get("/meetings/active");
  }

  async connectGoogle() {
    return this.client.post("/google/connect");
  }

  async getGoogleStatus() {
    return this.client.get("/google/status");
  }

  async syncGoogleCalendar() {
    return this.client.post("/google/sync");
  }

  async getGoogleUpcomingMeetings() {
    return this.client.get("/google/meetings/upcoming");
  }

  async joinGoogleMeeting(meetingId) {
    return this.client.post(`/google/meetings/${meetingId}/join`);
  }

  async leaveGoogleMeeting(meetingId) {
    return this.client.post(`/google/meetings/${meetingId}/leave`);
  }

  async disconnectGoogle() {
    return this.client.post("/google/disconnect");
  }
  async testCallSwitchConnection() {
    return this.client.post("/callswitch/test-connection");
  }

  async testCallSwitchAuth() {
    return this.client.post("/callswitch/test-auth");
  }

  async checkCallSwitchEnv() {
    return this.client.get("/callswitch/env-check");
  }

  async exploreCallSwitchData(options = {}) {
    return this.client.post("/callswitch/explore", options);
  }

  // Update the existing sync method to support test mode
  async syncCallSwitchRecordings(options = {}) {
    // If test flag is set, use the test endpoint
    if (options.test) {
      return this.client.post("/callswitch/sync", { test: true });
    }

    return this.client.post("/callswitch/sync", options);
  }

  async syncExtensionRecordings(extensionNumber, options = {}) {
    return this.client.post(
      `/callswitch/sync/extension/${extensionNumber}`,
      options,
    );
  }

  async syncSingleRecording(recordingId, extensionNumber) {
    return this.client.post(`/callswitch/sync/recording/${recordingId}`, {
      extensionNumber,
    });
  }

  async getWebexStatus() {
    return this.client.get("/integrations/webex/status");
  }

  async connectWebex() {
    return this.client.get("/integrations/webex/auth");
  }

  async disconnectWebex() {
    return this.client.post("/integrations/webex/disconnect");
  }

  async syncWebexRecordings(options = {}) {
    return this.client.post("/integrations/webex/sync", options);
  }

  // NEW: Update existing recordings with metadata
  async updateWebexMetadata(options = {}) {
    return this.client.post("/integrations/webex/update-metadata", {
      daysBack: options.daysBack || 7,
    });
  }

  // Webex Debug Methods
  async recreateWebexWebhook() {
    return this.client.post("/integrations/webex/webhook/recreate");
  }

  async testWebexWebhook() {
    return this.client.get("/integrations/webex/webhook/test");
  }
  async completeAuthorization(data) {
    return this.client.post("/lead-integration/authorize/complete", data);
  }

  // Add these methods to your ApiService class in api.js

  // ============================================================================
  // EMAIL INTEGRATION ENDPOINTS
  // ============================================================================
  // Add this method to the ApiService class
  async requalifyCall(callId) {
    return this.client.post(`/calls/${callId}/requalify`);
  }
  // Email Authentication & Connection
  async connectEmail(provider) {
    return this.client.get(`/email/auth/${provider}`);
  }

  async disconnectEmail(provider) {
    return this.client.post("/email/auth/disconnect", { provider });
  }

  async getEmailConnectionStatus() {
    return this.client.get("/email/auth/status");
  }

  async toggleEmailSync(provider, enabled) {
    return this.client.post("/email/auth/toggle-sync", { provider, enabled });
  }

  // Email Sync
  async syncEmails(options = {}) {
    return this.client.post("/email/sync", {
      provider: options.provider,
      syncType: options.syncType || "incremental",
      ...options,
    });
  }

  async getEmailSyncLogs(limit = 10) {
    return this.client.get("/email/sync/logs", { params: { limit } });
  }

  // Email Messages
  async getEmailMessages(filters = {}) {
    const params = new URLSearchParams({
      limit: filters.limit || "25",
      offset: filters.offset || "0",
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
    });
    return this.client.get(`/email/messages?${params}`);
  }

  async getEmailMessageById(messageId) {
    return this.client.get(`/email/messages/${messageId}`);
  }

  async reanalyzeEmail(messageId) {
    return this.client.post(`/email/messages/${messageId}/analyze`);
  }

  async searchEmails(query, filters = {}) {
    return this.client.post("/email/analytics/search", {
      query,
      filters,
    });
  }

  // Email Analytics
  async getEmailAnalyticsSummary(dateRange = {}) {
    const params = {};
    if (dateRange.startDate) params.startDate = dateRange.startDate;
    if (dateRange.endDate) params.endDate = dateRange.endDate;

    return this.client.get("/email/analytics/summary", { params });
  }

  async getEmailTrends(options = {}) {
    return this.client.get("/email/analytics/trends", { params: options });
  }

  async getEmailInsights(options = {}) {
    return this.client.get("/email/analytics/insights", { params: options });
  }

  // Legal Holds
  async getEmailLegalHolds(filters = {}) {
    return this.client.get("/email/legal-holds", { params: filters });
  }

  async createEmailLegalHold(holdData) {
    return this.client.post("/email/legal-holds", holdData);
  }

  async removeEmailLegalHold(holdId, removalData) {
    return this.client.post(`/email/legal-holds/${holdId}/remove`, removalData);
  }

  async exportEmailLegalHold(holdId) {
    return this.client.get(`/email/legal-holds/${holdId}/export`);
  }

  // Email Threads
  async getEmailThreads(filters = {}) {
    return this.client.get("/email/threads", { params: filters });
  }

  async getEmailThreadById(threadId) {
    return this.client.get(`/email/threads/${threadId}`);
  }

  // Email Entities
  async getEmailEntities(messageId) {
    return this.client.get(`/email/messages/${messageId}/entities`);
  }

  async searchEmailEntities(entityType, query) {
    return this.client.get("/email/entities/search", {
      params: { entityType, query },
    });
  }

  // Email Sentiment
  async getEmailSentiment(messageId) {
    return this.client.get(`/email/messages/${messageId}/sentiment`);
  }

  async getEmailSentimentTrends(filters = {}) {
    return this.client.get("/email/analytics/sentiment-trends", {
      params: filters,
    });
  }

  // Email Export
  async exportEmailData(filters = {}, format = "csv") {
    const response = await this.client.get("/email/export", {
      params: { ...filters, format },
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `emails-export-${Date.now()}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async exportEmailAnalytics(filters = {}, format = "pdf") {
    const response = await this.client.get("/email/analytics/export", {
      params: { ...filters, format },
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `email-analytics-${Date.now()}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Compliance & Security
  async getEmailComplianceReport(filters = {}) {
    return this.client.get("/email/compliance/report", { params: filters });
  }

  async flagEmailForReview(messageId, reason) {
    return this.client.post(`/email/messages/${messageId}/flag`, { reason });
  }

  async unflagEmail(messageId) {
    return this.client.delete(`/email/messages/${messageId}/flag`);
  }

  // Email Settings
  async getEmailSettings() {
    return this.client.get("/email/settings");
  }

  async updateEmailSettings(settings) {
    return this.client.put("/email/settings", settings);
  }

  async updateEmailSyncPreferences(preferences) {
    return this.client.put("/email/settings/sync", preferences);
  }

  async getCallQualification(callId) {
    return this.client.get(`/calls/${callId}/qualification`);
  }

  async updateCallQualification(callId, qualificationData) {
    return this.client.put(`/calls/${callId}/qualification`, qualificationData);
  }

  async manualQualifyCall(callId, isQualified, reasoning) {
    return this.client.post(`/calls/${callId}/qualify`, {
      isQualified,
      reasoning,
    });
  }

  // Email Notification Configuration endpoints
  async getNotificationConfigs() {
    return this.client.get("/email-notification-configs");
  }

  async updateNotificationConfig(notificationType, data) {
    return this.client.put(
      `/email-notification-configs/${notificationType}`,
      data,
    );
  }

  async testNotificationConfig(notificationType, recipients) {
    return this.client.post(
      `/email-notification-configs/${notificationType}/test`,
      {
        recipients,
      },
    );
  }

  // Add to api.js

  async getCallFilterOptions() {
    return this.client.get("/calls/filter-options");
  }

  async getCallRecordings(filters = {}) {
    return this.client.get("/calls", { params: filters });
  }
  // CallSwitch Extension Configuration
  /**
   * Discover and store extensions from CallSwitch
   * This triggers the backend to fetch from CallSwitch API and save to DB
   */
  async discoverCallSwitchExtensions() {
    return this.client.get("/callswitch/config/discover-extensions");
  }

  /**
   * Get stored extensions from database
   * @param {object} filters - Optional filters (autoDiscover: 'true' to auto-discover before fetching)
   */
  async getCallSwitchExtensions(filters = {}) {
    return this.client.get("/callswitch/config/extensions", {
      params: filters,
    });
  }

  /**
   * Update a single extension configuration
   */
  async updateCallSwitchExtensionConfig(extensionNumber, configData) {
    return this.client.put(
      `/callswitch/config/config/${extensionNumber}`,
      configData,
    );
  }

  /**
   * Bulk update multiple extension configurations
   */
  async bulkUpdateCallSwitchConfigs(configs) {
    return this.client.post("/callswitch/config/config/bulk", { configs });
  }

  /**
   * Delete extension configuration (sets to disabled)
   */
  async deleteCallSwitchExtensionConfig(extensionNumber) {
    return this.client.delete(`/callswitch/config/config/${extensionNumber}`);
  }

  /**
   * Manual sync trigger for recordings
   */
  async syncCallSwitchRecordings(options = {}) {
    return this.client.post("/callswitch/config/sync", options);
  }

  /**
   * Sync recordings for a specific extension
   */
  async syncCallSwitchExtensionRecordings(extensionNumber, options = {}) {
    return this.client.post(
      `/callswitch/config/extensions/${extensionNumber}/sync`,
      options,
    );
  }
  // Add these methods to the ApiService class

  async getCallSwitchRecordings(filters = {}) {
    return this.client.get("/calls/callswitch", { params: filters });
  }

  async getCallSwitchAnalytics(filters = {}) {
    return this.client.get("/calls/callswitch/analytics", { params: filters });
  }

  async exportCallSwitchCalls(filters = {}) {
    const response = await this.client.get("/calls/export/data", {
      params: { ...filters, platform: "callswitch" },
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `callswitch-export-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
  /**
   * Get sync status/stats
   */
  async getCallSwitchSyncStatus() {
    return this.client.get("/callswitch/config/sync/status");
  }

  /**
   * Analyze available recordings without downloading
   */
  async analyzeCallSwitchRecordings(options = {}) {
    return this.client.get("/callswitch/config/analyze-recordings", {
      params: options,
    });
  }
  async getSalesCallRecordings(filters = {}) {
    return this.client.get("/sales-calls", { params: filters });
  }

  async getSalesCallDetails(callId) {
    return this.client.get(`/sales-calls/${callId}`);
  }

  async getSalesCallAnalytics(filters = {}) {
    return this.client.get("/sales-calls/analytics", { params: filters });
  }

  async getSalesCallFilterOptions() {
    return this.client.get("/sales-calls/filter-options");
  }

  async updateSalesCallOutcome(callId, data) {
    // data: { manualOutcome, manualIsQualified, manualNotes }
    return this.client.patch(`/sales-calls/${callId}/outcome`, data);
  }

  async updateSalesCallMetadata(callId, data) {
    // data: { notes?, tags? }
    return this.client.patch(`/sales-calls/${callId}`, data);
  }

  async uploadSalesCallRecording(formData) {
    // formData: FormData with 'audio' file + externalUserId, externalAppointmentId, etc.
    return this.client.post("/sales-calls/upload", formData);
  }

  // Recording Studio endpoints
  async getRecordingStudioLicense() {
    return this.client.get("/recording-studio/license");
  }

  async getVoices(languageCode, extraParams = {}) {
    return this.client.get("/recording-studio/voices", {
      params: {
        languageCode,
        ...extraParams,
      },
    });
  }

  async createVoiceProject(projectData) {
    return this.client.post("/recording-studio/projects", projectData);
  }

  async getVoiceProjects(filters = {}) {
    return this.client.get("/recording-studio/projects", { params: filters });
  }

  async getVoiceProject(projectId) {
    return this.client.get(`/recording-studio/projects/${projectId}`);
  }

  async updateVoiceProject(projectId, data) {
    return this.client.put(`/recording-studio/projects/${projectId}`, data);
  }

  async deleteVoiceProject(projectId) {
    return this.client.delete(`/recording-studio/projects/${projectId}`);
  }

  async addVoiceBlock(projectId, blockData) {
    return this.client.post(
      `/recording-studio/projects/${projectId}/blocks`,
      blockData,
    );
  }

  async updateVoiceBlock(projectId, blockId, data) {
    return this.client.put(
      `/recording-studio/projects/${projectId}/blocks/${blockId}`,
      data,
    );
  }

  async deleteVoiceBlock(projectId, blockId) {
    return this.client.delete(
      `/recording-studio/projects/${projectId}/blocks/${blockId}`,
    );
  }

  /**
   * Layout-only patch: updates timelineStart / duration / orderIndex
   * WITHOUT resetting audioUrl or synthesis status.
   * Use this for drag-move, drag-resize, and sidebar reorder.
   */
  async patchVoiceBlockLayout(projectId, blockId, data) {
    return this.client.patch(
      `/recording-studio/projects/${projectId}/blocks/${blockId}/layout`,
      data,
    );
  }

  async synthesizeVoiceBlock(projectId, blockId) {
    return this.client.post(
      `/recording-studio/projects/${projectId}/blocks/${blockId}/synthesize`,
    );
  }

  async renderVoiceProject(projectId) {
    return this.client.post(`/recording-studio/projects/${projectId}/render`);
  }

  async getMusicLibrary(filters = {}) {
    return this.client.get("/recording-studio/music", { params: filters });
  }
  // In api.js
  async getSupportedFormats() {
    return this.client.get("/recording-studio/supported-formats");
  }

  /**
   * Re-transcribe a call recording
   */
  async reTranscribeCall(callId) {
    return this.client.post(`/calls/${callId}/retranscribe`);
  }

  /**
   * Re-analyze a call (sentiment + insights)
   */
  async reAnalyzeCall(callId) {
    return this.client.post(`/calls/${callId}/reanalyze`);
  }

  /**
   * Complete re-processing (transcribe + analyze + qualify)
   */
  async reProcessCall(callId) {
    return this.client.post(`/calls/${callId}/reprocess`);
  }

  // services/api.js - ADD THESE METHODS

  // ============================================================================
  // WEBEX USER CONFIGURATION
  // ============================================================================

  async discoverWebexUsers() {
    return this.client.get("/webex-users/discover");
  }

  async getWebexUsers(autoDiscover = false) {
    return this.client.get("/webex-users", {
      params: { autoDiscover: autoDiscover ? "true" : "false" },
    });
  }

  async updateWebexUserConfig(webexUserEmail, configData) {
    return this.client.put(
      `/webex-users/${encodeURIComponent(webexUserEmail)}/config`,
      configData,
    );
  }

  async bulkUpdateWebexUserConfigs(configs) {
    return this.client.post("/webex-users/config/bulk", { configs });
  }

  async setWebexUserDefaultPassword(
    webexUserEmail,
    password,
    applyToExisting = false,
  ) {
    return this.client.post(
      `/webex-users/${encodeURIComponent(webexUserEmail)}/password`,
      { password, applyToExisting },
    );
  }

  // ============================================================================
  // RECORDING PASSWORD PROTECTION
  // ============================================================================

  async setRecordingPassword(callId, password) {
    return this.client.post(`/calls/${callId}/password/set`, { password });
  }

  async removeRecordingPassword(callId) {
    return this.client.delete(`/calls/${callId}/password`);
  }

  async verifyRecordingPassword(callId, password) {
    return this.client.post(`/calls/${callId}/password/verify`, { password });
  }

  async checkRecordingPasswordStatus(callId) {
    return this.client.get(`/calls/${callId}/password/status`);
  }

  // Update getCallDetails to support password
  async getCallDetails(callId, password = null) {
    return this.client.get(`/calls/${callId}`, {
      params: password ? { password } : {},
    });
  }

  // ============================================================================
  // DARK WEB MONITORING
  // ============================================================================
  async getDarkWebOverview() {
    return this.client.get("/darkweb/overview");
  }
  async getDarkWebScore() {
    return this.client.get("/darkweb/score");
  }
  async getDarkWebScoreHistory(days = 30) {
    return this.client.get("/darkweb/score/history", { params: { days } });
  }
  async darkWebInstantSearch(value, assetType = "email") {
    return this.client.post("/darkweb/search", { value, assetType });
  }
  async getGlobalBreaches(params = {}) {
    return this.client.get("/darkweb/breaches/global", { params });
  }
  async getLatestGlobalBreaches(limit = 10) {
    return this.client.get("/darkweb/breaches/latest", { params: { limit } });
  }
  async refreshBreachCatalog() {
    return this.client.post("/darkweb/breaches/refresh");
  }
  async getDarkWebBreakdown() {
    return this.client.get("/darkweb/breakdown");
  }
  async getMonitoredAssets(assetType) {
    return this.client.get("/darkweb/assets", { params: { assetType } });
  }
  async addMonitoredAsset(data) {
    return this.client.post("/darkweb/assets", data);
  }
  async toggleMonitoredAsset(id) {
    return this.client.patch(`/darkweb/assets/${id}/toggle`);
  }
  async scanMonitoredAsset(id) {
    return this.client.post(`/darkweb/assets/${id}/scan`);
  }
  async getAssetExposures(id) {
    return this.client.get(`/darkweb/assets/${id}/exposures`);
  }
  async deleteMonitoredAsset(id) {
    return this.client.delete(`/darkweb/assets/${id}`);
  }
  async getDomainLookalikes() {
    return this.client.get("/darkweb/lookalikes");
  }
  async scanDomainLookalikes(domain) {
    return this.client.post("/darkweb/lookalikes/scan", { domain });
  }
  async updateLookalikeStatus(id, status) {
    return this.client.patch(`/darkweb/lookalikes/${id}`, { status });
  }
  async getExposures(params = {}) {
    return this.client.get("/darkweb/exposures", { params });
  }
  async acknowledgeExposure(id) {
    return this.client.post(`/darkweb/exposures/${id}/acknowledge`);
  }
  async resolveExposure(id, notes) {
    return this.client.post(`/darkweb/exposures/${id}/resolve`, { notes });
  }
  async getIncidents() {
    return this.client.get("/darkweb/incidents");
  }
  async createIncident(data) {
    return this.client.post("/darkweb/incidents", data);
  }
  async updateIncident(id, data) {
    return this.client.patch(`/darkweb/incidents/${id}`, data);
  }
}

export default new ApiService();
